import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import moment from 'moment';
import { Editor, Toolbar } from 'ngx-editor';
import {
  captureApiContext,
  CommunicationService,
  DispatchRecipient,
  EmailTemplate,
} from '../../services/communication-service/communication.service';
import { CsvRow, CsvService } from '../../services/csv-service/csv.service';
import { ModalService } from '../../services/modal/modal.service';
import { SettingsService } from '../../services/setting-service/setting.service';
import { TemplatePicker } from './template-picker/template-picker';

export interface DestinatarioInvalido {
  linha: number;
  valor: string;
  motivo: string;
}

const COLUNAS_EMAIL = ['email', 'e_mail', 'e-mail', 'endereco_de_email'];
const COLUNAS_NOME = ['nome', 'nome_completo', 'medico', 'destinatario'];

/** Validação simples e propositalmente permissiva de e-mail. */
const REGEX_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
  selector: 'app-dispatches',
  standalone: false,
  templateUrl: './dispatches.html',
  styleUrl: './dispatches.scss',
})
export class Dispatches implements OnInit, OnDestroy {
  private draftContext: () => boolean;

  private requireCurrentContext(): boolean {
    if (this.draftContext()) return true;
    this.loading = false;
    this.templates = [];
    this.limpar();
    this.modalService.error(
      'A sessão ou a API mudou. Reabra Disparos Pontuais antes de continuar.',
      'Recarregue os disparos',
    );
    return false;
  }

  loading: boolean = false;
  private dispatchKey: string | null = null;
  private dispatchPayload: string | null = null;
  enviando: boolean = false;

  templates: EmailTemplate[] = [];
  templateEscolhido: EmailTemplate | null = null;

  /**
   * Nome dado ao disparo para localizá-lo no histórico. Não vai para o
   * destinatário — é etiqueta interna, diferente do assunto do e-mail.
   */
  nomeDisparoControl = new FormControl('');

  assuntoControl = new FormControl('');

  /**
   * Último assunto preenchido automaticamente a partir de um template. Serve para
   * saber, ao voltar para "Nenhum", se o assunto na tela ainda é o do template
   * (e pode ser limpo) ou se o admin escreveu o próprio texto.
   */
  private assuntoDoTemplate: string | null = null;

  /** HTML enviado por upload, quando o admin não usa um template pronto. */
  htmlImportado: string | null = null;
  nomeArquivoHtml: string | null = null;
  mostrarPreview: boolean = false;

  /**
   * Corpo do e-mail como veio da fonte — template escolhido ou arquivo enviado.
   * Guardado separado do editado para que "Restaurar original" sempre tenha para
   * onde voltar.
   */
  private corpoOriginal: string | null = null;

  /** Conteúdo alterado no editor. `null` enquanto o admin não mexeu em nada. */
  corpoEditado: string | null = null;

  modoEdicao: boolean = false;
  editor: Editor = new Editor();
  conteudoControl = new FormControl('');

  /** Barra do editor: formatação de texto, sem nada que mexa em layout. */
  readonly toolbar: Toolbar = [
    ['bold', 'italic', 'underline'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['link'],
    ['align_left', 'align_center', 'align_right'],
  ];

  /** Campo do nome ao salvar o conteúdo editado como um template novo. */
  salvandoTemplate: boolean = false;
  nomeNovoTemplateControl = new FormControl('');

  /**
   * O HTML é exibido dentro de um `iframe` com `sandbox`, que bloqueia scripts e
   * acesso à mesma origem. Por isso a pré-visualização pode confiar no conteúdo
   * que o próprio administrador acabou de escolher.
   *
   * Serve aos dois caminhos — arquivo importado e template escolhido —, que são
   * mutuamente exclusivos.
   */
  htmlSeguro: SafeHtml | null = null;

  destinatarios: DispatchRecipient[] = [];
  invalidos: DestinatarioInvalido[] = [];
  selecionados = new Set<DispatchRecipient>();
  nomeArquivoCsv: string | null = null;

  displayedColumns: string[] = ['select', 'nome', 'email'];

  constructor(
    private communicationService: CommunicationService,
    private csvService: CsvService,
    private dialog: MatDialog,
    private modalService: ModalService,
    private sanitizer: DomSanitizer,
    public settingsService: SettingsService,
  ) {
    this.settingsService.title = 'Disparos Pontuais';
    this.draftContext = captureApiContext(settingsService);
  }

  async ngOnInit() {
    this.loading = true;

    try {
      const todos = await this.communicationService.getTemplates();
      if (!this.requireCurrentContext()) return;

      // Disparo pontual não tem relação com status de pedido: só entram os
      // templates de uso livre (evento, lançamento, comunicado). Os que estão
      // associados a um status pertencem ao motor de status, na tela de Pedidos.
      this.templates = todos.filter((template) => template.status_associado === null);
    } catch (error: any) {
      this.modalService.error(
        error?.error?.message || error?.message || 'Não foi possível carregar os templates.',
        'Erro ao carregar',
      );
    }

    this.loading = false;
  }

  ngOnDestroy() {
    this.editor.destroy();
  }

  // ------------------------------------------------------------------
  // Destinatários
  // ------------------------------------------------------------------

  /** Destaque da caixa enquanto um arquivo está sendo arrastado sobre ela. */
  arrastandoCsv: boolean = false;

  onDragOver(evento: DragEvent) {
    evento.preventDefault();
    if (this.enviando) return;
    this.arrastandoCsv = true;
  }

  onDragLeave(evento: DragEvent) {
    evento.preventDefault();
    if (this.enviando) return;
    this.arrastandoCsv = false;
  }

  onCsvDrop(evento: DragEvent) {
    evento.preventDefault();
    if (this.enviando) return;
    this.arrastandoCsv = false;
    this.onCsvSelected(evento.dataTransfer?.files ?? null);
  }

  async onCsvSelected(arquivos: FileList | null) {
    if (this.enviando) return;
    const arquivo = arquivos && arquivos.length ? arquivos[0] : null;

    if (!arquivo) {
      return;
    }

    this.loading = true;

    try {
      const conteudo = await this.csvService.parseFile(arquivo);
      this.processarDestinatarios(conteudo.headers, conteudo.rows, arquivo.name, conteudo.rowLines);
    } catch (error: any) {
      this.modalService.error(
        error?.message || 'Não foi possível ler o arquivo enviado.',
        'Erro na importação',
      );
    }

    this.loading = false;
  }

  private processarDestinatarios(
    headers: string[],
    rows: CsvRow[],
    nomeArquivo: string,
    rowLines: number[],
  ) {
    const colunaEmail = this.csvService.findColumn(headers, COLUNAS_EMAIL);

    if (!colunaEmail) {
      this.modalService.warning(
        'O arquivo precisa de uma coluna de e-mail. Nomes aceitos: e-mail, email.',
        'Coluna não encontrada',
      );
      return;
    }

    const colunaNome = this.csvService.findColumn(headers, COLUNAS_NOME);

    const validos: DispatchRecipient[] = [];
    const invalidos: DestinatarioInvalido[] = [];
    const jaVistos = new Set<string>();

    rows.forEach((row, indice) => {
      const numeroLinha = rowLines[indice]!;
      const email = (row[colunaEmail] ?? '').trim().toLowerCase();
      const nome = colunaNome ? (row[colunaNome] ?? '').trim() : '';

      if (!email) {
        invalidos.push({ linha: numeroLinha, valor: '', motivo: 'E-mail em branco' });
        return;
      }

      if (!REGEX_EMAIL.test(email)) {
        invalidos.push({ linha: numeroLinha, valor: email, motivo: 'E-mail em formato inválido' });
        return;
      }

      if (jaVistos.has(email)) {
        invalidos.push({ linha: numeroLinha, valor: email, motivo: 'Repetido no arquivo' });
        return;
      }

      jaVistos.add(email);
      validos.push({ nome: nome || email, email });
    });

    this.destinatarios = validos;
    this.invalidos = invalidos;
    this.nomeArquivoCsv = nomeArquivo;

    this.selecionados.clear();
    validos.forEach((destinatario) => this.selecionados.add(destinatario));

    if (invalidos.length) {
      this.modalService.warning(
        `${validos.length} destinatário(s) válidos e ${invalidos.length} com problema. ` +
          'Baixe a lista de inválidos para corrigir.',
        'Importação concluída',
      );
    }
  }

  baixarInvalidos() {
    if (!this.invalidos.length) {
      return;
    }

    const headers = ['linha', 'email', 'motivo'];
    const rows = this.invalidos.map((invalido) => ({
      linha: String(invalido.linha),
      email: invalido.valor,
      motivo: invalido.motivo,
    }));

    this.csvService.download(
      `destinatarios-invalidos-${moment().format('YYYY-MM-DD-HHmm')}.csv`,
      this.csvService.build(headers, rows),
    );
  }

  toggleSelecao(destinatario: DispatchRecipient) {
    if (this.enviando) return;
    if (this.selecionados.has(destinatario)) {
      this.selecionados.delete(destinatario);
    } else {
      this.selecionados.add(destinatario);
    }
  }

  todosSelecionados(): boolean {
    return (
      this.destinatarios.length > 0 &&
      this.destinatarios.every((destinatario) => this.selecionados.has(destinatario))
    );
  }

  alternarTodos() {
    if (this.enviando) return;
    if (this.todosSelecionados()) {
      this.selecionados.clear();
    } else {
      this.destinatarios.forEach((destinatario) => this.selecionados.add(destinatario));
    }
  }

  // ------------------------------------------------------------------
  // Conteúdo do e-mail
  // ------------------------------------------------------------------

  async onHtmlSelected(arquivos: FileList | null) {
    if (this.enviando) return;
    const arquivo = arquivos && arquivos.length ? arquivos[0] : null;

    if (!arquivo) {
      return;
    }

    this.loading = true;
    try {
      const conteudo = await this.csvService.readFile(arquivo);
      // O HTML importado substitui o template escolhido.
      this.removerTemplate();
      this.htmlImportado = conteudo;
      this.nomeArquivoHtml = arquivo.name;
      this.definirPreview(conteudo);
    } catch (error: any) {
      this.modalService.error(
        error?.message || 'Não foi possível ler o arquivo HTML.',
        'Erro na importação',
      );
    } finally {
      this.loading = false;
    }
  }

  removerHtml() {
    if (this.enviando) return;
    this.htmlImportado = null;
    this.nomeArquivoHtml = null;
    this.definirPreview(null);
  }

  /**
   * Único ponto que define o corpo do e-mail, venha de arquivo ou de template.
   * Trocar a fonte descarta qualquer edição em andamento — o conteúdo editado
   * pertence ao conteúdo de origem, não sobrevive à troca.
   */
  private definirPreview(html: string | null) {
    this.corpoOriginal = html;
    this.corpoEditado = null;
    this.modoEdicao = false;
    this.salvandoTemplate = false;
    this.htmlSeguro = html ? this.sanitizer.bypassSecurityTrustHtml(html) : null;

    if (!html) {
      this.mostrarPreview = false;
    }
  }

  /** O que será enviado: o conteúdo editado, se houver, senão o original. */
  get corpoAtual(): string | null {
    return this.corpoEditado ?? this.corpoOriginal;
  }

  /**
   * Se o conteúdo já difere do que veio da fonte. Durante a edição olha o
   * editor, e não `corpoEditado`, que só é preenchido ao concluir — sem isso o
   * "Restaurar original" ficaria desabilitado justamente enquanto se digita.
   */
  get conteudoAlterado(): boolean {
    const base = this.corpoOriginal ?? '';
    const atual = this.modoEdicao
      ? (this.conteudoControl.value ?? '')
      : (this.corpoEditado ?? base);

    return atual !== base;
  }

  /** Galeria de templates. */
  abrirGaleria() {
    if (this.enviando) return;
    const dialogRef = this.dialog.open(TemplatePicker, {
      width: '720px',
      maxWidth: '94vw',
      data: { templates: this.templates, selecionadoId: this.templateEscolhido?.id ?? null },
    });

    dialogRef.afterClosed().subscribe((template: EmailTemplate | undefined) => {
      if (template) {
        this.aplicarTemplate(template);
      }
    });
  }

  private aplicarTemplate(template: EmailTemplate) {
    if (this.enviando) return;
    // Escolher um template pronto descarta o HTML importado.
    this.removerHtml();

    this.templateEscolhido = template;
    this.definirPreview(template.html ?? null);

    // Cada template tem o próprio assunto padrão: trocar o template troca o assunto.
    this.assuntoControl.setValue(template.assunto_padrao);
    this.assuntoDoTemplate = template.assunto_padrao;
  }

  removerTemplate() {
    if (this.enviando) return;
    if (!this.templateEscolhido) {
      return;
    }

    this.templateEscolhido = null;
    this.definirPreview(null);

    // Só limpa o assunto se ele ainda for o do template, para não apagar um
    // texto escrito à mão.
    if (this.assuntoControl.value === this.assuntoDoTemplate) {
      this.assuntoControl.setValue('');
    }

    this.assuntoDoTemplate = null;
  }

  togglePreview() {
    this.mostrarPreview = !this.mostrarPreview;
  }

  // ------------------------------------------------------------------
  // Edição do conteúdo
  // ------------------------------------------------------------------

  editarConteudo() {
    if (this.enviando) return;
    this.conteudoControl.setValue(this.corpoAtual ?? '');
    this.modoEdicao = true;
    this.mostrarPreview = true;
  }

  /**
   * Fecha o editor guardando o que foi escrito. A edição vale só para este
   * disparo: o template de origem continua intacto.
   */
  concluirEdicao() {
    if (this.enviando) return;
    const conteudo = this.conteudoControl.value ?? '';
    if (!this.conteudoValido(conteudo)) {
      this.modalService.warning(
        'O conteúdo está vazio. Preencha o e-mail antes de concluir.',
        'Conteúdo obrigatório',
      );
      return;
    }

    this.corpoEditado = conteudo === this.corpoOriginal ? null : conteudo;
    this.htmlSeguro = this.sanitizer.bypassSecurityTrustHtml(this.corpoAtual ?? '');
    this.modoEdicao = false;
    this.salvandoTemplate = false;
  }

  restaurarOriginal() {
    if (this.enviando) return;
    this.corpoEditado = null;
    this.conteudoControl.setValue(this.corpoOriginal ?? '');
    this.htmlSeguro = this.corpoOriginal
      ? this.sanitizer.bypassSecurityTrustHtml(this.corpoOriginal)
      : null;
    this.salvandoTemplate = false;
  }

  abrirSalvarTemplate() {
    if (this.enviando) return;
    const base = this.templateEscolhido?.nome ?? this.nomeArquivoHtml ?? 'Novo template';
    this.nomeNovoTemplateControl.setValue(`${base} — cópia`);
    this.salvandoTemplate = true;
  }

  cancelarSalvarTemplate() {
    if (this.enviando) return;
    this.salvandoTemplate = false;
  }

  /**
   * Guarda o conteúdo ajustado como um template **novo**. O original nunca é
   * sobrescrito — quem entregou o template continua dono dele.
   */
  async salvarComoNovoTemplate() {
    if (!this.requireCurrentContext()) return;
    if (this.enviando) return;
    const nome = (this.nomeNovoTemplateControl.value || '').trim();
    const conteudo = this.modoEdicao ? (this.conteudoControl.value ?? '') : this.corpoAtual;

    if (!nome || !this.conteudoValido(conteudo)) {
      return;
    }

    this.loading = true;

    try {
      const criado = await this.communicationService.createTemplate({
        nome,
        assunto_padrao: (this.assuntoControl.value || '').trim(),
        html: conteudo,
      });

      this.templates = [...this.templates, criado];
      this.salvandoTemplate = false;
      this.modalService.success(
        `O template "${criado.nome}" foi criado e já pode ser reutilizado.`,
        'Template salvo',
      );
    } catch (error: any) {
      this.modalService.error(
        error?.error?.message || error?.message || 'Não foi possível salvar o template.',
        'Erro ao salvar',
      );
    }

    this.loading = false;
  }

  // ------------------------------------------------------------------
  // Envio
  // ------------------------------------------------------------------

  private conteudoValido(html: string | null): html is string {
    if (!html?.trim()) return false;
    const body = new DOMParser().parseFromString(html, 'text/html').body;
    return !!body.textContent?.replace(/\u00a0/g, ' ').trim() || !!body.querySelector('img[src]');
  }

  private payloadAtual() {
    const destinatarios = Array.from(this.selecionados)
      .map(({ nome, email }) => ({ nome, email: email.trim().toLowerCase() }))
      .sort((a, b) =>
        a.email < b.email ? -1 : a.email > b.email ? 1 : a.nome.localeCompare(b.nome),
      );
    const editado = this.corpoEditado !== null;
    return {
      nome: (this.nomeDisparoControl.value || '').trim(),
      assunto: (this.assuntoControl.value || '').trim(),
      template_id: editado ? null : (this.templateEscolhido?.id ?? null),
      html: editado ? this.corpoEditado : this.htmlImportado,
      destinatarios,
      disparo: 'pontual',
    };
  }

  podeEnviar(): boolean {
    const temFonte = !!this.templateEscolhido || this.conteudoValido(this.htmlImportado);
    const edicaoValida = this.corpoEditado === null || this.conteudoValido(this.corpoEditado);
    return (
      this.draftContext() &&
      this.selecionados.size > 0 &&
      this.selecionados.size <= 100 &&
      temFonte &&
      edicaoValida &&
      !!(this.assuntoControl.value || '').trim() &&
      !!(this.nomeDisparoControl.value || '').trim() &&
      !this.enviando &&
      !this.loading &&
      !this.modoEdicao
    );
  }

  confirmar() {
    if (!this.requireCurrentContext()) return;
    if (!this.podeEnviar()) {
      return;
    }

    const quantidade = this.selecionados.size;

    this.modalService.custom({
      type: 'warning',
      title: this.templateEscolhido?.nome,
      message: `O comunicado será enviado para ${quantidade} destinatário(s).`,
      showCloseButton: true,
      listButtons: [
        { textButton: 'Cancelar', fill: 'outline' },
        { textButton: 'Enviar', fill: 'solid', action: () => this.enviar() },
      ],
    });
  }

  async enviar() {
    if (!this.requireCurrentContext()) return;
    if (!this.podeEnviar()) return;
    const payload = this.payloadAtual();
    const signature = JSON.stringify(payload);
    if (!this.dispatchKey || signature !== this.dispatchPayload) {
      this.dispatchKey = crypto.randomUUID();
      this.dispatchPayload = signature;
    }
    this.enviando = true;
    try {
      const resultado = await this.communicationService.sendDispatch({
        ...payload,
        chave_idempotencia: this.dispatchKey,
      });
      const falhas = resultado.falhas?.length ?? 0;
      if (falhas) {
        this.modalService.warning(
          `${resultado.enviados} enviado(s) e ${falhas} com falha.`,
          'Disparo concluído com falhas',
        );
      } else {
        this.modalService.success(
          `${resultado.enviados} envio(s) aceito(s) pelo provedor de e-mail.`,
          'Disparo concluído',
        );
        // Uma conclusão antiga não pode apagar um rascunho diferente, mesmo se ele mudou por código.
        if (signature === JSON.stringify(this.payloadAtual())) {
          this.dispatchKey = null;
          this.dispatchPayload = null;
          this.enviando = false;
          this.limpar();
        }
      }
    } catch (error: any) {
      this.modalService.error(
        error?.error?.message || error?.message || 'Não foi possível concluir o disparo.',
        'Erro no disparo',
      );
    } finally {
      this.enviando = false;
    }
  }

  limpar() {
    if (this.enviando) return;
    this.destinatarios = [];
    this.invalidos = [];
    this.selecionados.clear();
    this.nomeArquivoCsv = null;
    this.nomeDisparoControl.setValue('');
    this.assuntoControl.setValue('');
    this.assuntoDoTemplate = null;
    this.templateEscolhido = null;
    this.removerHtml();
  }
}
